import * as React from "react";
import { useParams } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SkeletonTable } from "@/components/feel/Skeleton";
import { EmptyState, ErrorState } from "@/components/feel/EmptyState";
import { Activity } from "lucide-react";

/**
 * Audit — reverse-chronological event log.
 *
 * Phase 8 pass:
 * - Skeleton loading, EmptyState + ErrorState.
 * - Virtualised rows past 100 (react-virtual). Under 100, plain table (no
 *   overhead). We fetch the first 500 by default and let the virtualiser
 *   handle scrolling.
 */
export default function Audit() {
  const { companyId } = useParams();
  const [rows, setRows] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [filters, setFilters] = React.useState({ modules: [] });
  const [selectedModule, setSelectedModule] = React.useState("");
  const [selectedActor, setSelectedActor] = React.useState("");

  const load = React.useCallback(async () => {
    if (!companyId) return;
    setError(null);
    const params = { limit: 500 };
    if (selectedModule) params.module = selectedModule;
    if (selectedActor) params.actor_id = selectedActor;
    try {
      const { data } = await api.get(`/companies/${companyId}/audit`, { params });
      setRows(data.events);
      setFilters(data.filters || { modules: [] });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "The audit trail failed to load.");
    }
  }, [companyId, selectedModule, selectedActor]);

  React.useEffect(() => { load(); }, [load]);

  // Live-refresh on any event.
  React.useEffect(() => {
    const on = () => load();
    window.addEventListener("fable:event", on);
    window.addEventListener("fable:refetch", on);
    return () => {
      window.removeEventListener("fable:event", on);
      window.removeEventListener("fable:refetch", on);
    };
  }, [load]);

  const actors = React.useMemo(() => {
    if (!rows) return [];
    const map = new Map();
    for (const r of rows) if (r.actor_id) map.set(r.actor_id, r.actor_name || r.actor_id);
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [rows]);

  const isLoading = rows === null && !error;
  const isEmpty = rows !== null && rows.length === 0 && !error;
  const useVirtual = rows && rows.length > 100;

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1200px]" data-testid="audit-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="micro-label">Trail</div>
          <h1 className="mt-1 text-[22px] font-bold tracking-tight text-brand-ink">Audit</h1>
          <div className="mt-1 text-[12px] text-brand-muted">
            Reverse-chronological.{" "}
            <span className="mono-num text-brand-ink">{rows?.length ?? "—"}</span> latest{useVirtual ? " · virtualised" : ""}.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select className="field-input !w-40 !h-8" value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  data-testid="audit-filter-module">
            <option value="">All modules</option>
            {filters.modules.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="field-input !w-56 !h-8" value={selectedActor}
                  onChange={(e) => setSelectedActor(e.target.value)}
                  data-testid="audit-filter-actor">
            <option value="">All actors</option>
            {actors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      </div>

      {error && (
        <ErrorState
          title="Audit didn't load"
          body={error}
          onRetry={load}
          className="mt-4"
          testid="audit-error"
        />
      )}

      {isLoading && (
        <div className="mt-4 card-panel overflow-hidden">
          <SkeletonTable rows={10} cols={5} />
        </div>
      )}

      {isEmpty && (
        <div className="mt-4 card-panel">
          <EmptyState
            icon={Activity}
            eyebrow="No trail yet"
            title="No events match the current filter"
            body="Try clearing filters or wait for the next action — audit rows arrive live."
            testid="audit-empty"
          />
        </div>
      )}

      {rows && rows.length > 0 && !useVirtual && (
        <div className="mt-4 card-panel overflow-hidden">
          <div className="table-scroll"><table className="fable-table" data-testid="audit-table">
            <thead>
              <tr>
                <th style={{width: "16%"}}>Timestamp</th>
                <th style={{width: "12%"}}>Module</th>
                <th style={{width: "18%"}}>Actor</th>
                <th style={{width: "16%"}}>Action</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <AuditRow key={r.id} r={r} />
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {rows && rows.length > 0 && useVirtual && (
        <VirtualAudit rows={rows} />
      )}
    </div>
  );
}

const ROW_H = 40; // px — matches the dense-table row height (h-9-ish).

function VirtualAudit({ rows }) {
  const parentRef = React.useRef(null);
  const virt = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_H,
    overscan: 8,
  });

  return (
    <div className="mt-4 card-panel overflow-hidden" data-testid="audit-virtual">
      <div className="grid grid-cols-[16%_12%_18%_16%_1fr] gap-0 px-3 py-2 text-[10.5px] uppercase tracking-wider text-brand-muted font-semibold border-b border-brand-rule bg-brand-canvas-2">
        <div>Timestamp</div><div>Module</div><div>Actor</div><div>Action</div><div>Summary</div>
      </div>
      <div ref={parentRef} className="max-h-[calc(100vh-260px)] overflow-y-auto" style={{ contain: "strict" }}>
        <div style={{ height: `${virt.getTotalSize()}px`, width: "100%", position: "relative" }}>
          {virt.getVirtualItems().map((v) => {
            const r = rows[v.index];
            return (
              <div key={r.id}
                   data-testid={`audit-row-${r.id}`}
                   style={{
                     position: "absolute", top: 0, left: 0, width: "100%",
                     transform: `translateY(${v.start}px)`,
                     height: `${v.size}px`,
                   }}
                   className="grid grid-cols-[16%_12%_18%_16%_1fr] items-center gap-0 px-3 border-b border-brand-rule hover:bg-brand-canvas transition-colors">
                <div className="mono-num text-[11px] text-brand-muted">{r.ts?.replace("T", " ").slice(0, 19)}</div>
                <div><span className="chip mono-num">{r.module}</span></div>
                <div><span className="text-[12.5px] font-medium text-brand-ink truncate">{r.actor_name || "—"}</span></div>
                <div className="mono-num text-[11.5px] text-brand-violet truncate">{r.action}</div>
                <div className="text-[12.5px] text-brand-ink truncate">{r.summary}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AuditRow({ r }) {
  return (
    <tr data-testid={`audit-row-${r.id}`}>
      <td className="mono-num text-[11px] text-brand-muted">{r.ts?.replace("T", " ").slice(0, 19)}</td>
      <td><span className="chip mono-num">{r.module}</span></td>
      <td><span className="text-[12.5px] font-medium text-brand-ink">{r.actor_name || "—"}</span></td>
      <td className="mono-num text-[11.5px] text-brand-violet">{r.action}</td>
      <td className="text-[12.5px] text-brand-ink">{r.summary}</td>
    </tr>
  );
}
